import React, { Fragment, cloneElement, useState, useEffect, useCallback } from "react";
import { Drawer, useMediaQuery } from '@mui/material';
import { matchPath, useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router';


import {
  List,
  CreateButton,
  TopToolbar,
  ExportButton,
  sanitizeListRestProps,
  useResourceContext,
  downloadCSV,
  useLocaleState
} from "react-admin";
import jsonExport from "jsonexport/dist";
import alasql from "alasql";
import PostBulkActionButtons from "./ui/PostBulkActionButtons";
import DefaultListGrid from "./ui/DefaultListGrid";
import TabedListGrid from "./ui/TabedListGrid";
import ResourceCreate from "./Create";
import ReviewEdit from "./ReviewEdit";
import {
  generateFilterFieldLocale
} from "./factory";
import { roles, generateSQLRoleFields } from '../../config';



const exporter = (records, fetchRelatedRecords, dataProvider, resource) => {
  var list = [];
  var value = [];
  var rearray = {};
  const role = localStorage.getItem("role");

  list = ["business_uuid"];
  value = ["business_uuid"];

  var d = records.map(function (obj) {
    list.map(function (key, index) {
      rearray[list[index]] = obj[value[index]];
    });

    return rearray;
  });

  jsonExport(
    d,
    {
      headers: list,
    },
    (err, csv) => {
      const BOM = "\uFEFF";

      downloadCSV(`${BOM} ${csv}`, resource);
    }
  );
};

const ListActions = ({
  currentSort,
  className,
  resource,
  filters,
  displayedFilters,
  exporter, // you can hide ExportButton if exporter = (null || false)
  filterValues,
  permanentFilter,
  hasCreate, // you can hide CreateButton if hasCreate = false
  basePath,
  selectedIds,
  onUnselectItems,
  showFilter,
  maxResults,
  total,
  ...rest
}) => (
  <TopToolbar className={className} {...sanitizeListRestProps(rest)}>
    {filters &&
      cloneElement(filters, {
        resource,
        showFilter,
        displayedFilters,
        filterValues,
        context: "button",
      })}
    <CreateButton basePath={basePath} />
    <ExportButton
      disabled={total === 0}
      resource={resource}
      sort={currentSort}
      filter={{ ...filterValues, ...permanentFilter }}
      exporter={exporter}
      maxResults={maxResults}
    />
    {/* Add your custom actions 
      <Button
          onClick={() => { alert('Your custom action'); }}
          label="Show calendar"
      >
          <IconEvent />
      </Button>
      */}
  </TopToolbar>
);

ListActions.defaultProps = {
  selectedIds: [],
  onUnselectItems: () => null,
};

// const exporter = posts => {
//   const postsForExport = posts.map(post => {
//       const { backlinks, author, ...postForExport } = post; // omit backlinks and author
//       //postForExport.author_name = post.author.name; // add a field
//       return postForExport;
//   });
//   jsonExport(postsForExport, {
//       //headers: ['id', 'title', 'author_name', 'body'] // order fields in the export
//   }, (err, csv) => {
//       downloadCSV(csv, 'data'); // download as 'posts.csv` file
//   });
// }

const ResourceList = (props) => {

  const [loading, setLoading] = useState(true);
  const [resourcesFields, setResourcesFields] = useState([]);
  const [resourcePermistions, setResourcePermistions] = useState([]);
  const [resourcesFilters, setResourcesFilters] = useState([]);
  const [stateColumn, setStateColumn] = useState(null);
  const [stateValue, setStateValue] = useState(0);

  useEffect(() => {
    async function fetchData() {
      const { resource, locale } = props;
      const role = localStorage.getItem('role');

      const sqlRoleFields = generateSQLRoleFields(['show', 'edit', 'delete'], roles);


      const resourcePermistions = await alasql.promise(`
        select init_values,extra,resource_name,is_expand,expand_resource, expand_source, expand_destination,list_is_tab,list_is_calendar,list_tabs,list_tab_filter,list_tab_filter_value,resource_name,inline_update,inline_create,
        ${sqlRoleFields}
        from resource_permistions
        where resource_name ='${resource}'
      `);

      const resourcesFields = await alasql.promise(`
        select watched_column,source_name,field_widget_type, field_meta, css_class from resources 
        WHERE 
        resource_name ='${resource}' AND is_list_field =true 
          AND (role_${role} = 'write' or role_${role} = 'read')
      `);

      const filterData = alasql(`
        select watched_column,source_name, filter_widget_type, input_widget_type, field_meta, annotation, workflows_read, 'none' as workflow,is_filter_show
        from resources
        where resource_name = '${resource}' and is_filter = true
        AND (role_${role} = 'write' or role_${role} = 'read')`);

      const resourcesFilters = filterData.map((f, index) => {
        const key = `filter-${f.source_name || 'field'}-${index}`;
        return renderFilterField(f, index, key);
      });

      setResourcesFields(resourcesFields);
      setResourcePermistions(resourcePermistions);
      setResourcesFilters(resourcesFilters);
      setLoading(false);
    }

    fetchData();
  }, [props]);



  // Utility function to add alwaysOn globally
  const enhanceWithAlwaysOn = (element, condition = true, key = null) => {
    // Ensure that the element is a valid React element
    if (React.isValidElement(element)) {
      return React.cloneElement(element, {
        ...(key && { key }),
        ...(condition && { alwaysOn: true }),
      });
    }
    return element; // If it's not a valid React element, return it as is
  };

  const renderFilterField = (f, index, customKey) => {
    f.input_widget_type = f.filter_widget_type;
    
    // Generate the filter field element with proper parameters
    // generateFilterFieldLocale(f, index, record, id, locale, customKey)
    const filterElement = generateFilterFieldLocale(f, index, undefined, props.id, props.locale, customKey);

    // Enhance the element with alwaysOn if needed and ensure proper key
    return enhanceWithAlwaysOn(filterElement, f.is_filter_show, customKey);
  };


  const onTabChange = (Column, Value) => {
    if (stateColumn !== Column || Value !== stateValue) {
      setStateColumn(Column);
      setStateValue(Value);
    }
  };

  const isXSmall = useMediaQuery(theme =>
    theme.breakpoints.down('sm')
  );
  const isSmall = useMediaQuery(theme => theme.breakpoints.down('md'));

  const location = useLocation();
  const navigate = useNavigate();

  const handleClose = useCallback(() => {
    navigate(`/${props.resource}/`);
  }, [navigate]);

  const matchEdit = matchPath(`/${props.resource}/:id`, location.pathname);
  const matchCreate = matchPath(`/${props.resource}/create`, location.pathname);


  const renderInlineCreate = () => {
    return resourcePermistions[0].inline_create && (<Drawer
      variant="persistent"
      open={!!matchCreate}
      anchor="right"
      onClose={handleClose}
      sx={{ zIndex: 100 }}
    >
      {/* To avoid any errors if the route does not match, we don't render at all the component in this case */}
      {!!matchCreate && (
        <ResourceCreate
          onCancel={handleClose}
        />
      )}
    </Drawer>);
  };

  const renderInlineUpdate = () => {
    return resourcePermistions[0].inline_update && (<Drawer
      variant="persistent"
      open={!!matchEdit}
      anchor="right"
      onClose={handleClose}
      sx={{ zIndex: 100 }}
    >
      {/* To avoid any errors if the route does not match, we don't render at all the component in this case */}
      {!!matchEdit && (
        <ReviewEdit
          id={(matchEdit).params.id}
          onCancel={handleClose}
        />
      )}
    </Drawer>);
  };



  let expandEdit = false;
  let expandEditColumn = '';
  let expandEditValue = '';

  if (resourcePermistions[0] && resourcePermistions[0].extra && resourcePermistions[0].extra !== '') {
    const extra = JSON.parse(resourcePermistions[0].extra);

    if (extra && extra.Edit === 'true') {
      expandEdit = true;
      expandEditColumn = extra.EditColumn;
      expandEditValue = extra.EditValue;
    }
  }


  if (loading) return 'loading ...';


  let sortKey = "id";
  let sortValue = "DESC";

  
  if (resourcePermistions.length > 0) {
    const { init_values } = resourcePermistions[0];



    if (init_values) {

      try {
        const parsedInitValues = JSON.parse(init_values);
        if (parsedInitValues.sortKey) {
          sortKey = parsedInitValues.sortKey;
        }
        if (parsedInitValues.sortValue) {
          sortValue = parsedInitValues.sortValue;
        }

     

      } catch (error) {
        console.error("Error parsing init_values:", error);
      }
    }
  }


  if (resourcePermistions[0].list_is_tab) {
    resourcePermistions[0].list_tab_filter_value = parseInt(resourcePermistions[0].list_tab_filter_value);

    return (
      <Fragment>
        <List

sort={{ field: sortKey, order: sortValue }}

          sx={{
            flexGrow: 1,
            transition: (theme) =>
              theme.transitions.create(['all'], {
                duration: theme.transitions.duration.enteringScreen,
              }),
            marginRight: !!matchEdit ? '400px' : 0,
            marginTop: isSmall ? undefined : -1,
          }}
          {...props}
          bulkActionButtons={
            <PostBulkActionButtons
              {...props}
              resource={props.resource}
              Column={stateColumn}
              Value={stateValue}
            />
          }
          filters={resourcesFilters}
          filterDefaultValues={{
            [resourcePermistions[0].list_tab_filter]: resourcePermistions[0].list_tab_filter_value,
          }}
        >
          <TabedListGrid
            {...props}
            optimized

            sx={{
              '& .column-groups': {
                md: { display: 'none' },
                lg: { display: 'table-cell' },
              },
            }}
            onTabChange={onTabChange}
            resourcePermistions={resourcePermistions[0]}
            resourcesFields={resourcesFields}
          />
        </List>

        {renderInlineCreate()}
        {renderInlineUpdate()}




      </Fragment>
    );
  } else {
    // Modify filters to add `alwaysOn` for those with `is_filter_show` set to true


    return (
      <Fragment>
        <List

          sort={{ field: sortKey, order: sortValue }}


          {...props}
          exporter={false}
          //  filters={resourcesFilters}
          filters={resourcesFilters}


        >
          <DefaultListGrid
            optimized
            rowClick={false}
            sx={{
              '& .column-groups': {
                md: { display: 'none' },
                lg: { display: 'table-cell' },
              },
            }}
            {...props}
            resourcePermistions={resourcePermistions[0]}
            resourcesFields={resourcesFields}
            bulkActionButtons={
              expandEdit ? (
                <PostBulkActionButtons
                  {...props}
                  resource={props.resource}
                  Column={expandEditColumn}
                  Value={expandEditValue}
                />
              ) : false

            }
          />
        </List>

        {renderInlineCreate()}
        {renderInlineUpdate()}

      </Fragment>
    );
  }


}



/*

export default reduxForm({
  form: 'list',
  enableReinitialize: true,

})(ResourceList)
*/

const UiResourceList = (props) => {
  //console.log(props);
  const resource = useResourceContext();
  const [locale] = useLocaleState();

  return <ResourceList resource={resource} locale={locale} {...props} />;
};

export default UiResourceList;







